import React, { useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { YesNoRow } from "../ui/yes-no-toggle";
import { User, Loader2 } from "lucide-react";
import DateDropdownPicker from "../ui/DateDropdownPicker";
import RequiredLabel from "../ui/RequiredLabel";
import { maritalStatuses, educationLevels, livingConditions } from "../../utils/mockData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PersonalInfoSection = ({ data, onChange, fieldErrors = {} }) => {
  const [postalLoading, setPostalLoading] = useState({});

  const handleInputChange = (field, value) => {
    onChange({ [field]: value });
  };

  const errClass = (field) => fieldErrors[field] ? "border-red-500 ring-1 ring-red-500" : "";

  const handlePostalCodeChange = useCallback(async (prefix, value) => {
    handleInputChange(`${prefix}PostalCode`, value);
    const clean = (value || "").replace(/\D/g, "");
    if (clean.length === 6) {
      setPostalLoading(prev => ({ ...prev, [prefix]: true }));
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/postal-lookup/${clean}`);
        if (res.ok) {
          const data = await res.json();
          onChange({
            [`${prefix}PostalCode`]: clean,
            [`${prefix}County`]: data.county || "",
            [`${prefix}City`]: data.city || ""
          });
        }
      } catch { /* ignore */ }
      setPostalLoading(prev => ({ ...prev, [prefix]: false }));
    }
  }, [onChange]);

  const handleFindPostalCode = useCallback(async (prefix) => {
    const street = data[`${prefix}Street`] || "";
    const number = data[`${prefix}Number`] || "";
    const city = data[`${prefix}City`] || data[`${prefix}County`] || "";
    if (!street || !city) return;

    setPostalLoading(prev => ({ ...prev, [`${prefix}Find`]: true }));
    try {
      const params = new URLSearchParams({ street, number, city });
      if (data[`${prefix}County`]) params.append("county", data[`${prefix}County`]);
      const res = await fetch(`${BACKEND_URL}/api/public/address-to-postal?${params}`);
      if (res.ok) {
        const result = await res.json();
        if (result.postalCode) {
          const updates = { [`${prefix}PostalCode`]: result.postalCode };
          if (result.county && !data[`${prefix}County`]) updates[`${prefix}County`] = result.county;
          if (result.city && !data[`${prefix}City`]) updates[`${prefix}City`] = result.city;
          onChange(updates);
        }
      }
    } catch { /* ignore */ }
    setPostalLoading(prev => ({ ...prev, [`${prefix}Find`]: false }));
  }, [data, onChange]);

  return (
    <Card className="mb-6">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-green-50 border-b">
        <CardTitle className="flex items-center gap-2 text-blue-900">
          <User className="h-5 w-5" />
          Date Personale Solicitant
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Nume și Prenume */}
          <div className="space-y-2">
            <RequiredLabel htmlFor="lastName">Nume</RequiredLabel>
            <Input
              id="lastName"
              data-testid="personal-lastName"
              value={data.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              placeholder="Introduceți numele"
              className={errClass('lastName')}
            />
          </div>

          <div className="space-y-2">
            <RequiredLabel htmlFor="firstName">Prenume</RequiredLabel>
            <Input
              id="firstName"
              data-testid="personal-firstName"
              value={data.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              placeholder="Introduceți prenumele"
              className={errClass('firstName')}
            />
          </div>

          {/* Sex */}
          <div className="space-y-2">
            <RequiredLabel>Sex</RequiredLabel>
            <Select
              value={data.sex}
              onValueChange={(value) => handleInputChange('sex', value)}
            >
              <SelectTrigger data-testid="personal-sex" className={errClass('sex')}>
                <SelectValue placeholder="Selectați" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Masculin">Masculin</SelectItem>
                <SelectItem value="Feminin">Feminin</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Data nașterii */}
          <div className="space-y-2">
            <RequiredLabel>Data Nașterii</RequiredLabel>
            <DateDropdownPicker
              id="birthDate"
              value={data.birthDate}
              onChange={(val) => handleInputChange('birthDate', val)}
              hasError={!!fieldErrors.birthDate}
            />
          </div>

          {/* CNP */}
          <div className="space-y-2">
            <RequiredLabel htmlFor="cnp">CNP</RequiredLabel>
            <Input
              id="cnp"
              data-testid="personal-cnp"
              value={data.cnp}
              onChange={(e) => handleInputChange('cnp', e.target.value)}
              placeholder="Codul numeric personal"
              maxLength={13}
              className={errClass('cnp')}
            />
          </div>

          {/* Cetățenie */}
          <div className="space-y-2">
            <Label htmlFor="citizenship">Cetățenie</Label>
            <Input
              id="citizenship"
              data-testid="personal-citizenship"
              value={data.citizenship}
              onChange={(e) => handleInputChange('citizenship', e.target.value)}
              placeholder="Ex: Română"
            />
          </div>

          {/* Act de identitate */}
          <div className="space-y-2">
            <Label htmlFor="idType">Tip Act Identitate</Label>
            <Select
              value={data.idType}
              onValueChange={(value) => handleInputChange('idType', value)}
            >
              <SelectTrigger data-testid="personal-idType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CI">Carte de Identitate</SelectItem>
                <SelectItem value="Pașaport">Pașaport</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <RequiredLabel htmlFor="idSeries">Serie Act</RequiredLabel>
            <Input
              id="idSeries"
              data-testid="personal-idSeries"
              value={data.idSeries}
              onChange={(e) => handleInputChange('idSeries', e.target.value)}
              placeholder="Ex: AB"
              className={errClass('idSeries')}
            />
          </div>

          <div className="space-y-2">
            <RequiredLabel htmlFor="idNumber">Număr Act</RequiredLabel>
            <Input
              id="idNumber"
              data-testid="personal-idNumber"
              value={data.idNumber}
              onChange={(e) => handleInputChange('idNumber', e.target.value)}
              placeholder="Numărul actului"
              className={errClass('idNumber')}
            />
          </div>

          <div className="space-y-2">
            <RequiredLabel htmlFor="idIssuer">Emitent</RequiredLabel>
            <Input
              id="idIssuer"
              data-testid="personal-idIssuer"
              value={data.idIssuer}
              onChange={(e) => handleInputChange('idIssuer', e.target.value)}
              placeholder="Ex: SPCLEP București"
              className={errClass('idIssuer')}
            />
          </div>

          {/* Data Eliberare - Dropdown Zi/Luna/An */}
          <div className="space-y-2">
            <RequiredLabel>Data Eliberare</RequiredLabel>
            <DateDropdownPicker
              id="idIssueDate"
              value={data.idIssueDate}
              onChange={(val) => handleInputChange('idIssueDate', val)}
              hasError={!!fieldErrors.idIssueDate}
            />
          </div>

          {/* Data Expirare - Dropdown Zi/Luna/An */}
          <div className="space-y-2">
            <RequiredLabel>Data Expirare</RequiredLabel>
            <DateDropdownPicker
              id="idExpiryDate"
              value={data.idExpiryDate}
              onChange={(val) => handleInputChange('idExpiryDate', val)}
              maxYear={2040}
              hasError={!!fieldErrors.idExpiryDate}
            />
          </div>

          {/* Stare civilă */}
          <div className="space-y-2">
            <Label htmlFor="maritalStatus">Stare Civilă</Label>
            <Select
              value={data.maritalStatus}
              onValueChange={(value) => handleInputChange('maritalStatus', value)}
            >
              <SelectTrigger data-testid="personal-maritalStatus">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {maritalStatuses.map(status => (
                  <SelectItem key={status} value={status}>{status}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Studii */}
          <div className="space-y-2">
            <Label htmlFor="education">Studii Finalizate</Label>
            <Select
              value={data.education}
              onValueChange={(value) => handleInputChange('education', value)}
            >
              <SelectTrigger data-testid="personal-education">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {educationLevels.map(level => (
                  <SelectItem key={level} value={level}>{level}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Copii */}
          <div className="space-y-2">
            <Label htmlFor="childrenCount">Număr Copii în Întreținere</Label>
            <Input
              id="childrenCount"
              data-testid="personal-childrenCount"
              type="number"
              value={data.childrenCount}
              onChange={(e) => handleInputChange('childrenCount', e.target.value)}
              min="0"
            />
          </div>

          {/* Numele de fată mamă */}
          <div className="space-y-2">
            <Label htmlFor="motherMaidenName">Numele de Fată al Mamei</Label>
            <Input
              id="motherMaidenName"
              data-testid="personal-motherMaidenName"
              value={data.motherMaidenName}
              onChange={(e) => handleInputChange('motherMaidenName', e.target.value)}
              placeholder="Pentru identificare bancară"
            />
          </div>

          {/* Condiție locativă */}
          <div className="space-y-2">
            <Label htmlFor="livingCondition">Condiție Locativă</Label>
            <Select
              value={data.livingCondition}
              onValueChange={(value) => handleInputChange('livingCondition', value)}
            >
              <SelectTrigger data-testid="personal-livingCondition">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {livingConditions.map(condition => (
                  <SelectItem key={condition} value={condition}>{condition}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* ── ADRESĂ DOMICILIU ── */}
        <div className="mt-6 p-4 border border-blue-200 rounded-lg bg-blue-50/30">
          <h3 className="text-sm font-semibold text-blue-900 mb-4">Adresă Domiciliu</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Județ / Sector *</Label>
              <Input
                data-testid="dom-county"
                value={data.domCounty || ""}
                onChange={(e) => handleInputChange('domCounty', e.target.value)}
                placeholder="Ex: Sector 2 / Cluj"
                className={errClass('domCounty')}
              />
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Localitate</Label>
              <Input
                data-testid="dom-city"
                value={data.domCity || ""}
                onChange={(e) => handleInputChange('domCity', e.target.value)}
                placeholder="Ex: București / Cluj-Napoca"
              />
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Strada *</Label>
              <Input
                data-testid="dom-street"
                value={data.domStreet || ""}
                onChange={(e) => handleInputChange('domStreet', e.target.value)}
                placeholder="Numele străzii"
                className={errClass('domStreet')}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Nr. *</Label>
              <Input
                data-testid="dom-number"
                value={data.domNumber || ""}
                onChange={(e) => handleInputChange('domNumber', e.target.value)}
                placeholder="Nr."
                className={errClass('domNumber')}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Bloc</Label>
              <Input
                data-testid="dom-block"
                value={data.domBlock || ""}
                onChange={(e) => handleInputChange('domBlock', e.target.value)}
                placeholder="Bloc"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Scara</Label>
              <Input
                data-testid="dom-staircase"
                value={data.domStaircase || ""}
                onChange={(e) => handleInputChange('domStaircase', e.target.value)}
                placeholder="Sc."
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Etaj</Label>
              <Input
                data-testid="dom-floor"
                value={data.domFloor || ""}
                onChange={(e) => handleInputChange('domFloor', e.target.value)}
                placeholder="Et."
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Apartament</Label>
              <Input
                data-testid="dom-apartment"
                value={data.domApartment || ""}
                onChange={(e) => handleInputChange('domApartment', e.target.value)}
                placeholder="Ap."
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Cod Poștal</Label>
              <div className="flex gap-1">
                <div className="relative flex-1">
                  <Input
                    data-testid="dom-postalCode"
                    value={data.domPostalCode || ""}
                    onChange={(e) => handlePostalCodeChange('dom', e.target.value)}
                    placeholder="Ex: 021587"
                    maxLength={6}
                  />
                  {postalLoading.dom && <Loader2 className="h-3 w-3 animate-spin absolute right-2 top-3 text-blue-500" />}
                </div>
                <button
                  type="button"
                  onClick={() => handleFindPostalCode('dom')}
                  disabled={postalLoading.domFind || (!data.domStreet && !data.domCity)}
                  className="px-2 h-9 border border-blue-300 rounded bg-blue-50 hover:bg-blue-100 disabled:opacity-40 text-blue-600 text-xs font-medium shrink-0"
                  title="Caută codul poștal după stradă și localitate"
                >
                  {postalLoading.domFind ? <Loader2 className="h-3 w-3 animate-spin" /> : "Caută"}
                </button>
              </div>
              <p className="text-xs text-blue-500">Completați strada + localitate apoi apăsați "Caută"</p>
            </div>
          </div>
        </div>

        {/* ── ADRESĂ REȘEDINȚĂ ── */}
        <div className="mt-4 p-4 border border-green-200 rounded-lg bg-green-50/30">
          <h3 className="text-sm font-semibold text-green-900 mb-1">Adresă Reședință</h3>
          <p className="text-xs text-gray-500 mb-3">Completați doar dacă diferă de domiciliu. Obligatorii: Județ/Sector, Strada, Nr., Cod Poștal</p>
          <div className="flex items-center space-x-2 mb-4">
            <Checkbox
              id="resSameAsDomicile"
              data-testid="res-same-as-domicile"
              checked={data.resSameAsDomicile || false}
              onCheckedChange={(checked) => {
                if (checked) {
                  onChange({
                    resSameAsDomicile: true,
                    resCounty: data.domCounty || "", resCity: data.domCity || "",
                    resStreet: data.domStreet || "", resNumber: data.domNumber || "",
                    resBlock: data.domBlock || "", resStaircase: data.domStaircase || "",
                    resFloor: data.domFloor || "", resApartment: data.domApartment || "",
                    resPostalCode: data.domPostalCode || ""
                  });
                } else {
                  onChange({ resSameAsDomicile: false });
                }
              }}
            />
            <Label htmlFor="resSameAsDomicile" className="text-sm font-medium cursor-pointer">Reședința coincide cu domiciliul (preia automat datele de la domiciliu)</Label>
          </div>
          {!data.resSameAsDomicile && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Județ / Sector *</Label>
              <Input
                data-testid="res-county"
                value={data.resCounty || ""}
                onChange={(e) => handleInputChange('resCounty', e.target.value)}
                placeholder="Ex: Sector 1 / Ilfov"
              />
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Localitate</Label>
              <Input
                data-testid="res-city"
                value={data.resCity || ""}
                onChange={(e) => handleInputChange('resCity', e.target.value)}
                placeholder="Ex: București / Voluntari"
              />
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Strada *</Label>
              <Input
                data-testid="res-street"
                value={data.resStreet || ""}
                onChange={(e) => handleInputChange('resStreet', e.target.value)}
                placeholder="Numele străzii"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Nr. *</Label>
              <Input
                data-testid="res-number"
                value={data.resNumber || ""}
                onChange={(e) => handleInputChange('resNumber', e.target.value)}
                placeholder="Nr."
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Bloc</Label>
              <Input
                data-testid="res-block"
                value={data.resBlock || ""}
                onChange={(e) => handleInputChange('resBlock', e.target.value)}
                placeholder="Bloc"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Scara</Label>
              <Input
                data-testid="res-staircase"
                value={data.resStaircase || ""}
                onChange={(e) => handleInputChange('resStaircase', e.target.value)}
                placeholder="Sc."
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Etaj</Label>
              <Input
                data-testid="res-floor"
                value={data.resFloor || ""}
                onChange={(e) => handleInputChange('resFloor', e.target.value)}
                placeholder="Et."
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Apartament</Label>
              <Input
                data-testid="res-apartment"
                value={data.resApartment || ""}
                onChange={(e) => handleInputChange('resApartment', e.target.value)}
                placeholder="Ap."
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Cod Poștal *</Label>
              <div className="flex gap-1">
                <div className="relative flex-1">
                  <Input
                    data-testid="res-postalCode"
                    value={data.resPostalCode || ""}
                    onChange={(e) => handlePostalCodeChange('res', e.target.value)}
                    placeholder="Ex: 077190"
                    maxLength={6}
                  />
                  {postalLoading.res && <Loader2 className="h-3 w-3 animate-spin absolute right-2 top-3 text-blue-500" />}
                </div>
                <button
                  type="button"
                  onClick={() => handleFindPostalCode('res')}
                  disabled={postalLoading.resFind || (!data.resStreet && !data.resCity)}
                  className="px-2 h-9 border border-blue-300 rounded bg-blue-50 hover:bg-blue-100 disabled:opacity-40 text-blue-600 text-xs font-medium shrink-0"
                  title="Caută codul poștal după stradă și localitate"
                >
                  {postalLoading.resFind ? <Loader2 className="h-3 w-3 animate-spin" /> : "Caută"}
                </button>
              </div>
              <p className="text-xs text-blue-500">Completați strada + localitate apoi apăsați "Caută"</p>
            </div>
          </div>
          )}
        </div>

        {/* Contact */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div className="space-y-2">
            <RequiredLabel htmlFor="phone">Telefon</RequiredLabel>
            <Input
              id="phone"
              data-testid="personal-phone"
              value={data.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="Numărul de telefon"
              className={errClass('phone')}
            />
          </div>

          <div className="space-y-2">
            <RequiredLabel htmlFor="email">Email</RequiredLabel>
            <Input
              id="email"
              data-testid="personal-email"
              type="email"
              value={data.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="Adresa de email"
              className={errClass('email')}
            />
          </div>
        </div>

        {/* PEP */}
        <div className="mt-6">
          <div className="p-4 border border-yellow-200 rounded-lg bg-yellow-50 space-y-3">
            <YesNoRow
              label="Persoană Expusă Politic (PEP)"
              value={data.isPoliticallyExposed ?? null}
              onChange={(v) => handleInputChange('isPoliticallyExposed', v)}
              testid="pep"
            >
              <p className="text-xs text-yellow-800 mt-1">
                Sunteți sau ați fost în ultimele 12 luni într-o funcție publică importantă (ministru, parlamentar, primar, etc.)
                sau membru al familiei/asociat apropiat al unei astfel de persoane.
              </p>
            </YesNoRow>
            {data.isPoliticallyExposed === true && (
              <div className="mt-2 pt-3 border-t border-yellow-200 space-y-4">
                <p className="text-sm font-semibold text-yellow-900">Secțiune dedicată PEP</p>
                <div className="space-y-2">
                  <Label className="text-xs">Calitate PEP</Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { v: 'asociat_pep', l: 'Asociat PEP' },
                      { v: 'membru_familie', l: 'Membru familie (până la gradul 2)' },
                      { v: 'persoana_publica', l: 'Persoană publică' },
                    ].map(opt => (
                      <button
                        key={opt.v}
                        type="button"
                        data-testid={`pep-quality-${opt.v}`}
                        onClick={() => handleInputChange('pepQuality', opt.v)}
                        className={`px-4 py-1.5 text-sm rounded-md border transition-colors ${data.pepQuality === opt.v ? 'bg-yellow-600 text-white border-yellow-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-yellow-100'}`}
                      >
                        {opt.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Data devenirii PEP</Label>
                    <DateDropdownPicker value={data.pepDate} onChange={(val) => handleInputChange('pepDate', val)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Funcția publică deținută</Label>
                    <Input value={data.pepFunction || ''} onChange={(e) => handleInputChange('pepFunction', e.target.value)} placeholder="Funcția publică" data-testid="pep-function" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PersonalInfoSection;
